import { type FC, useState } from "react";
import {
  Toolbar,
  Checkbox,
  Button,
  Typography,
  Divider,
  Menu,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ActionMenuItem,
  ToolbarProgress,
  useConfirmAction,
} from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useUpdateContentsMutation,
  useDeleteContentsMutation,
} from "@/partner/entities/contents";
import { AddToCollectionButton } from "./AddToCollectionButton";
import { AddToProductButton } from "@/partner/features/products";

export interface SelectedCollectionItem {
  id: string;
  collectionIds: string[];
  productIds: string[];
}

interface CollectionsListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  totalCount: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: SelectedCollectionItem[];
  onCloseSelected: () => void;
}

// Порт референсного CollectionsToolbar: select-all + меню «Действия»
// (опубликовать/скрыть/удалить + «Добавить в коллекцию» — коллекции тоже
// кладутся в коллекции).
export const CollectionsListToolbar: FC<CollectionsListToolbarProps> = ({
  indeterminate,
  numSelected,
  totalCount,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const [updateContents, { loading: updateLoading }] =
    useUpdateContentsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("collections.toolbar.updateSuccess"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["CollectionsList"],
    });

  const [deleteContents, { loading: deleteLoading }] =
    useDeleteContentsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("collections.toolbar.deleteSuccess"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["CollectionsList"],
    });

  const selectedIds = selectedItems.map((item) => item.id);

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === totalCount}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: { xs: 0, sm: "16px" },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{ mx: 1.25, height: "2em" }}
      />

      <Button
        color="inherit"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={numSelected === 0}
      >
        {t("common.actions.actions")}
      </Button>

      <Menu
        id="collections-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            updateContents({
              variables: {
                ids: selectedIds,
                input: { publishAt: new Date().toISOString() },
              },
            });
            handleClose();
          }}
        >
          {t("common.actions.publish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            updateContents({
              variables: { ids: selectedIds, input: { publishAt: null } },
            });
            handleClose();
          }}
        >
          {t("common.actions.unpublish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={deleteLoading}
          onClick={() => {
            confirm({
              title: t("common.actions.delete"),
              text: t("collections.toolbar.deleteConfirm"),
              // Промис наружу: диалог держится открытым с полосой прогресса,
              // пока идёт запрос, и закрывается сам по успеху.
              onConfirm: () =>
                deleteContents({ variables: { ids: selectedIds } }),
            });
            handleClose();
          }}
        >
          {t("common.actions.delete")}
        </ActionMenuItem>

        <AddToCollectionButton
          menuItem
          disabled={numSelected === 0}
          selectedItems={selectedItems}
          refetchQueries={["CollectionsList"]}
          onCompleted={() => {
            onCloseSelected();
            handleClose();
          }}
        />

        <AddToProductButton
          menuItem
          disabled={numSelected === 0}
          selectedItems={selectedItems}
          refetchQueries={["CollectionsList"]}
          onCompleted={() => {
            onCloseSelected();
            handleClose();
          }}
        />
      </Menu>

      <ToolbarProgress loading={updateLoading || deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
